/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Topological Ordering & Graph Traversal
   Kahn's algorithm implementation for pure canonical data.
   ═══════════════════════════════════════════════════════════════ */

import type { CanonicalGraph, NodeId } from './types';

export interface TopologicalSortResult {
  readonly success: boolean;
  readonly order: NodeId[];
  readonly cycleNodes?: NodeId[];
}

/**
 * Computes topological ordering of a graph using Kahn's algorithm.
 * Returns success=false and the cycle-involved nodes if a cycle exists.
 */
export function computeTopologicalOrder(graph: CanonicalGraph): TopologicalSortResult {
  const nodeIds = Object.keys(graph.nodes);
  const inDegree: Record<NodeId, number> = {};
  const adjacency: Record<NodeId, NodeId[]> = {};

  for (const id of nodeIds) {
    inDegree[id] = 0;
    adjacency[id] = [];
  }

  for (const edge of Object.values(graph.edges)) {
    // Only count edges whose source and target nodes actually exist
    if (graph.nodes[edge.sourceNodeId] && graph.nodes[edge.targetNodeId]) {
      adjacency[edge.sourceNodeId].push(edge.targetNodeId);
      inDegree[edge.targetNodeId] = (inDegree[edge.targetNodeId] || 0) + 1;
    }
  }

  // Find all nodes with zero in-degree
  const queue: NodeId[] = [];
  for (const id of nodeIds) {
    if (inDegree[id] === 0) {
      queue.push(id);
    }
  }

  const order: NodeId[] = [];
  while (queue.length > 0) {
    const current = queue.shift()!;
    order.push(current);

    const neighbors = adjacency[current] || [];
    for (const neighbor of neighbors) {
      inDegree[neighbor]--;
      if (inDegree[neighbor] === 0) {
        queue.push(neighbor);
      }
    }
  }

  if (order.length !== nodeIds.length) {
    // Cycle detected
    const cycleNodes = nodeIds.filter((id) => (inDegree[id] || 0) > 0);
    return {
      success: false,
      order: [],
      cycleNodes,
    };
  }

  return {
    success: true,
    order,
  };
}
